// Widget view-key grammar — the single source of truth for parsing the opaque
// `view` strings the widget table is keyed by, and for the ownership/access
// decision derived from them. Imported by BOTH enforcement points (the core
// API's WidgetViewAccessService and the client's useWidgetViewAccess) so the
// server guard and the client's edit affordances can never drift — the same
// principle as the notes `viewerCanEdit` helper.
//
// The keys are PERSISTED VALUES — never rename them:
//   widget-view-member:<userId>        /my-work            private to that user
//   widget-view-team:<id>              team Dashboard tab  shared
//   widget-view-team:<id>-overview     team Overview tab   shared
//   widget-view-workflow:<id>          workflow Dashboard  shared
//   widget-view-entity-view:<viewId>   a dashboard-type entity view   shared
//
// The `entity-view` kind keys off the entity view's own id, so a scope can
// carry many dashboards. A workflow's built-in Dashboard tab keeps the
// `workflow` key it was created under, which is why both kinds exist.
//
// Only `team` keys have a variant form (`-overview` — the only variant
// today), recognized as a known suffix from the closed TEAM_VIEW_VARIANTS
// set. Entity ids may CONTAIN hyphens (core's nanoid alphabet includes `-`
// and `_`; legacy 24-hex ObjectIds don't), so a team key is never split at a
// hyphen — that truncated a hyphenated team id, misrouted its default board
// and its manager grant. `member`/`workflow`/`entity-view` tails are entity
// ids verbatim — never variant-split.
//
// Pure and synchronous by design: the async inputs (role verdict, team-manager
// membership, privacy bypass) are resolved by each caller from its own data
// and passed in as the `actor`.

const VIEW_PREFIX = "widget-view-";

// The closed set of team-key variants. A variant is a persisted key suffix —
// add only, never rename — and must never collide with the tail of a real id
// (fixed-length nanoid/hex ids make an id ending in `-overview` implausible).
const TEAM_VIEW_VARIANTS = ["overview"] as const;

export type WidgetViewKind = "member" | "team" | "workflow" | "entity-view";

export interface ParsedWidgetView {
  kind: WidgetViewKind;
  /** User id for `member`, team/workflow id otherwise. */
  entityId: string;
  /** Suffix after the entity id (`overview`) — team keys only. */
  variant: string | null;
}

export interface WidgetViewScope {
  kind: WidgetViewKind;
  entityId: string;
  variant: string | null;
}

export interface WidgetViewOwnership {
  visibility: "private" | "shared";
  /** Owning user id — private views only. */
  ownerId: string | null;
  /** Shared views only; null for private views and unparseable keys. */
  scope: WidgetViewScope | null;
}

export interface WidgetViewActor {
  /** Workspace user id (=== client `currentUser.userId`). */
  userId: string;
  /** Resolved `widgets.manage` verdict. */
  canManageWidgets: boolean;
  /**
   * Sync predicate, caller-resolved: may the actor manage this team
   * (workspace `manage.teams` OR a manager of the team). The server resolves
   * it from TeamsService.canManageTeam, the client from Team.viewerCanManage.
   */
  managesTeam: (teamId: string) => boolean;
  /**
   * Owner role, or Admin with the workspace `adminOverrideEnabled` toggle
   * on. The client always passes false.
   */
  privacyBypass: boolean;
  /**
   * Caller-resolved ACL for `entity-view:` keys. Non-null → the decision for
   * a WORKSPACE-scoped dashboard (its creator, share levels, visibility and
   * the privacy bypass, all resolved by the caller from the view row); null
   * → the view is workflow-scoped or unknown here, and the shared rule below
   * applies. The server resolves it from the entity_view row + its shares,
   * the client from `EntityView.viewerAccessLevel`. Optional because the
   * grammar cannot know a view's scope kind without the row.
   */
  entityViewAccess?: (viewId: string) => { canRead: boolean; canWrite: boolean } | null;
}

export interface WidgetViewAccess {
  canRead: boolean;
  canWrite: boolean;
  ownership: WidgetViewOwnership;
}

/**
 * Parse a widget view key. Returns null for anything that isn't one of the
 * known-kind keys — callers must treat null as "unknown key", not an error.
 */
export function parseWidgetView(view: string): ParsedWidgetView | null {
  if (!view.startsWith(VIEW_PREFIX)) return null;
  const rest = view.slice(VIEW_PREFIX.length);
  const colon = rest.indexOf(":");
  if (colon === -1) return null;
  const kind = rest.slice(0, colon);
  if (
    kind !== "member" &&
    kind !== "team" &&
    kind !== "workflow" &&
    kind !== "entity-view"
  )
    return null;
  const tail = rest.slice(colon + 1);
  if (!tail) return null;
  // Only team keys carry a variant, and it is a KNOWN suffix — ids can
  // contain hyphens, so splitting at one truncates the id. A tail that ends
  // in no known variant is the entity id verbatim, hyphens and all.
  if (kind === "team") {
    for (const variant of TEAM_VIEW_VARIANTS) {
      const suffix = `-${variant}`;
      if (tail.length > suffix.length && tail.endsWith(suffix)) {
        return { kind, entityId: tail.slice(0, -suffix.length), variant };
      }
    }
  }
  return { kind, entityId: tail, variant: null };
}

/**
 * Derive ownership from a view key. Unparseable keys are treated as shared
 * with no scope — reads stay open and writes require `widgets.manage`, which
 * preserves today's behavior for any future/unknown key.
 */
export function ownershipForView(view: string): WidgetViewOwnership {
  const parsed = parseWidgetView(view);
  if (!parsed) return { visibility: "shared", ownerId: null, scope: null };
  if (parsed.kind === "member") {
    return { visibility: "private", ownerId: parsed.entityId, scope: null };
  }
  return {
    visibility: "shared",
    ownerId: null,
    scope: { kind: parsed.kind, entityId: parsed.entityId, variant: parsed.variant },
  };
}

/**
 * The access decision — one place, two enforcers.
 *
 * Private views: owner-only read AND write (no permission needed by the
 * owner), with the platform privacy-bypass as the only exception.
 * Entity-view boards whose view row carries its own ACL (a workspace-scoped
 * dashboard): whatever the caller resolved from that row — `widgets.manage`
 * grants nothing on someone else's dashboard.
 * Every other shared view: read is open to everyone; write requires
 * `widgets.manage` OR — team scope only — managing that team. Workflow views
 * get no team-manager grant (workflows have no manager).
 */
export function widgetViewAccess(view: string, actor: WidgetViewActor): WidgetViewAccess {
  const ownership = ownershipForView(view);
  if (ownership.visibility === "private") {
    const allowed = actor.userId === ownership.ownerId || actor.privacyBypass;
    return { canRead: allowed, canWrite: allowed, ownership };
  }
  if (ownership.scope?.kind === "entity-view") {
    const acl = actor.entityViewAccess?.(ownership.scope.entityId);
    if (acl) return { ...acl, ownership };
  }
  const canWrite =
    actor.canManageWidgets ||
    (ownership.scope?.kind === "team" && actor.managesTeam(ownership.scope.entityId));
  return { canRead: true, canWrite, ownership };
}

/**
 * Build a widget view key. The inverse of `parseWidgetView`, so the two
 * halves of the grammar sit in one file and no caller hand-concatenates a
 * persisted value.
 */
export function widgetViewKey(
  kind: WidgetViewKind,
  entityId: string,
  variant?: string,
): string {
  const base = `${VIEW_PREFIX}${kind}:${entityId}`;
  return variant ? `${base}-${variant}` : base;
}
